import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography, useTheme } from '@mui/material'
import TitleContext from '../../TitleContext'
import { useTranslation } from 'react-i18next'
import DynamicBreadcrumbs from '../Elements/DynamicBreadcrumbs'
import { Page } from '../Elements/PageMargins'
import { FetchPlaylistResponse, PlaylistPlaylistTypeEnum } from '../../swagger'
import { playlistApi } from '../../api/swagger'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import useLoadingContext from '../../hooks/useLoadingContext'
import EmptyPage from '../Elements/EmptyPage'
import PlaylistActionCard from '../Card/PlaylistActionCard'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import SortableItem from './SortableItem'
import { handleDragEnd } from './utils/handleDragEnd'
import { SnackbarSeverity } from '../Alerts/SnackbarAlert'
import { usePlaylistContext } from '../../hooks/usePlaylistContext'
import { useDeletePlaylistItem } from '../../hooks/playlist/useDeletePlaylistItem'
import DeletePlaylistConfirmationModal from '../Modals/playlist/DeletePlaylistConfirmationModal'
import { useMountEffect } from '../../hooks/useMountEffect'
import { truncateText } from '../../utils/stringUtility'

export type PlaylistType = `${PlaylistPlaylistTypeEnum}`

interface PlaylistDetailProps {
  playlistType: PlaylistType
}

const PlaylistDetail: React.FC<PlaylistDetailProps> = ({
  playlistType: parentComponent,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { playlistKey = '0' } = useParams<{ playlistKey: string }>()

  const { useTitleEffect } = React.useContext(TitleContext)
  const {
    handleError,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
  } = useAppSnackbar()
  const {
    Playlist: { updatePlaylistItemsOrder: updatePlaylistItemsOrderLoadingId },
  } = useLoadingIds()

  const [playlistItemToDelete, setPlaylistItemToDelete] = useState<number>()

  const { playlist, isLoading, updatePlaylist, refetch } = usePlaylistContext({
    playlistKey: +playlistKey,
  })
  const onSuccess = () => {
    const playlistUpdated = {
      ...playlist,
      playlistItems: playlist?.playlistItems.filter(
        (item) => item.playlistItemKey !== playlistItemToDelete
      ),
    } as FetchPlaylistResponse
    updatePlaylist(playlistUpdated)
    /** should be refreshed by the context */
    playlistInitialValue.current = playlistUpdated
    setPlaylistItemToDelete(undefined)
  }
  const { deletePlaylistItem } = useDeletePlaylistItem(
    playlistItemToDelete,
    onSuccess
  )

  const isBusiness = parentComponent === 'BusinessCenter'

  const title = isBusiness
    ? t('Business.PlaylistDetail.Title', 'Playlist Details')
    : t('Learning.PlaylistDetail.Title', 'Playlist Details')
  useTitleEffect(title)

  const playlistsLabel = isBusiness
    ? t('Business.Playlists.BreadCrumb', 'Playlists')
    : t('Learning.Playlists.BreadCrumb', 'Playlists')

  const defaultErrorMessageOrder = t(
    'PlaylistDetails.Error.UpdatePlaylistOrder',
    'Error occurred while sorting playlist'
  )

  const navigateToBack = () => {
    navigate({
      pathname: `/${parentComponent
        .replace('Center', '')
        .toLowerCase()}/playlists`,
    })
  }

  const playlistInitialValue = useRef<FetchPlaylistResponse>()

  /** DRAG AND DROP / SORTABLE */

  /**
   * Setup drag sensors for mouse and touch with some activation delay on touch
   */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  /** Save playlist order to backend */
  const updateOrder = async () => {
    const itemIdsInNewOrder = playlist?.playlistItems.map(
      ({ asset }) => asset?.assetKey
    ) as number[]
    try {
      await playlistApi.updatePlaylistItemsOrder({
        playlistKey: +playlistKey,
        body: {
          widenAssetKeys: itemIdsInNewOrder,
        },
      })
      setSnackbarMessage('Order updated successfully.')
      setSnackbarSeverity(SnackbarSeverity.Success)
      setSnackbarState?.(true)
    } catch (error) {
      await handleError(error, defaultErrorMessageOrder)
    }
  }
  /**
   * Hook to trigger the update order request with loading state
   */
  const { triggerFetch: updateOrderPlaylist } = useLoadingContext({
    asyncFunction: updateOrder,
    loadingId: updatePlaylistItemsOrderLoadingId,
  })

  useMountEffect(refetch)

  useEffect(() => {
    /** If there is no playlist loaded yet, exit early */
    if (!playlist) return

    const currentKey = playlist.playlist.playlistKey
    const initialKey = playlistInitialValue.current?.playlist.playlistKey
    const items = playlist.playlistItems ?? []
    const initialItems = playlistInitialValue.current?.playlistItems ?? []
    /** If this is the first render, or if the user navigated to a different playlist
     *  (detected by comparing playlistKey), update the initial snapshot and exit.
     */
    if (!playlistInitialValue.current || initialKey !== currentKey) {
      playlistInitialValue.current = playlist
      return
    }

    /* If the number of current items has changed compared to the initial items *and* the current key remains the same,
     update the `playlistInitialValue` reference to reflect the new `playlistItems`.
     This ensures the internal state stays in sync with external changes to the list,
     without resetting the context of the current playlist (since the key did not change).
     This also covers the edge case where all items are removed (length becomes 0),
     ensuring the internal state is properly cleared.*/
    if (initialItems.length !== items.length && initialKey === currentKey) {
      playlistInitialValue.current.playlistItems = items
      return
    }

    /**
     * Determine whether the current items differ from the initial snapshot.
     * We check both the length and the order of playlistItemKey values.
     */
    const hasPlaylistOrderChanged = items.some(
      (item, index) =>
        item.playlistItemKey !== initialItems[index]?.playlistItemKey
    )
    /**
     * If there are items present and their order/content has changed
     * compared to the initial snapshot, trigger the updateOrderPlaylist function
     * and update the snapshot to the current state.
     *
     */
    if (
      items.length > 0 &&
      hasPlaylistOrderChanged &&
      items.length === initialItems.length
    ) {
      updateOrderPlaylist()
      playlistInitialValue.current = playlist
    }
  }, [playlist, updateOrderPlaylist])

  useEffect(() => {
    if (
      +playlistKey === +(playlist?.playlist.playlistKey ?? '0') &&
      playlist?.hasMissingAssets &&
      playlist?.playlist.isShared
    ) {
      // handleMessage is not working properly for this case.
      setSnackbarMessage(
        t(
          'PlaylistDetail.Info.MissingAssets',
          `Some assets are missing because you don't have access to them.`
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Info)
      setSnackbarState?.(true)
    }
  }, [
    playlist?.hasMissingAssets,
    playlist?.playlist.isShared,
    playlist?.playlist.playlistKey,
    playlistKey,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    t,
  ])

  const playlistDetailLabel = isBusiness
    ? t('Business.PlaylistDetail.Title', '{{name}}', {
        name: playlist?.playlist.name ?? '',
      })
    : t('Learning.PlaylistDetail.Title', '{{name}}', {
        name: playlist?.playlist.name ?? '',
      })

  if (!playlist) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'PlaylistDetail.Message.NoPlaylistData',
              'No Playlist information available.'
            )}
          </Typography>
        }
        isLoading={isLoading}
      />
    )
  }

  const isShared = playlist.playlist.isShared

  return (
    <Page>
      <DynamicBreadcrumbs
        breadcrumbs={[
          { label: playlistsLabel, onClick: navigateToBack },
          { label: truncateText(playlistDetailLabel ?? '') },
        ]}
      />
      <Box
        display="grid"
        gap={2}
        width="100%"
        sx={{
          [theme.breakpoints.down(800)]: {
            gridTemplateColumns: '1fr',
          },
          [theme.breakpoints.up(800)]: {
            gridTemplateColumns: '40% 60%',
          },
        }}
      >
        <PlaylistActionCard
          playlist={playlist.playlist}
          isShared={isShared}
          itemCount={playlist.playlistItems.length}
          updatePlaylistNameContext={(value: string) => {
            updatePlaylist({
              ...playlist,
              playlist: { ...playlist.playlist, name: value },
            })
          }}
          firstPlaylistItemKey={playlist.playlistItems[0]?.playlistItemKey}
        />
        {playlist.playlistItems.length > 0 ? (
          <Box
            sx={(theme) => ({
              [theme.breakpoints.up(700)]: {
                maxHeight: '70vh',
                overflowY: 'auto',
              },
              willChange: 'transform',
              touchAction: 'none',
            })}
          >
            <Box
              sx={{
                display: 'grid',
                gridAutoRows: theme.spacing(12),
                gap: theme.spacing(1),
                alignItems: 'stretch',
              }}
              data-testid={'playlist-container'}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => handleDragEnd(e, playlist, updatePlaylist)}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              >
                <SortableContext
                  items={playlist.playlistItems.map(
                    (playlistItem) => playlistItem.playlistItemKey
                  )}
                  strategy={verticalListSortingStrategy}
                  disabled={isShared}
                >
                  {playlist.playlistItems.map((item) => (
                    <SortableItem
                      key={item.playlistItemKey}
                      item={item}
                      isShared={isShared}
                      onClick={() =>
                        navigate(
                          `${
                            isBusiness ? '/business' : '/learning'
                          }/playlist/content/${playlist.playlist.playlistKey}/${
                            item.playlistItemKey
                          }`
                        )
                      }
                      onDeleteClick={() =>
                        setPlaylistItemToDelete(item.playlistItemKey)
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: theme.spacing(40),
              color: 'text.primary',
              textAlign: 'center',
              py: 4,
            }}
          >
            <Inventory2OutlinedIcon
              sx={{
                fontSize: 64,
                mb: 1,
                color: theme.palette.textOrIcon.tooltipButton,
              }}
            />
            <Typography variant="body2" sx={{ fontSize: '1.1rem' }}>
              {t(
                'PlaylistDetail.Message.Empty',
                'There are currently no items in this playlist.'
              )}
            </Typography>
          </Box>
        )}
      </Box>
      {!!playlistItemToDelete && (
        <DeletePlaylistConfirmationModal
          isOpen
          onCancel={() => setPlaylistItemToDelete(undefined)}
          onConfirm={deletePlaylistItem}
          label="Playlist Item"
          confirmationMessage="This will delete the selected playlist Item from your playlist. Do you wish to continue"
        />
      )}
    </Page>
  )
}

export default PlaylistDetail
